import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { PostShiftReviewFlow } from "@src/appV2/PostShiftReview/PostShiftReviewFlow";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { getCreditProgressBarLabel } from "../../../../EmergencyCredits/utils/getCreditProgressBarLabel";
import { MY_SHIFTS_FULL_PATH } from "../../../path";
import { useEmergencyCreditStats } from "./hooks/useEmergencyCreditStats";
import { buildScoreDataForEmergencyCreditsClockOutFlow } from "./YourClipboardScore/utils/buildScoreDataForEmergencyCreditsClockOutFlow";
import { YourClipboardScorePage } from "./YourClipboardScore/YourClipboardScorePage";
import { YourEmergencyCreditsPage } from "./YourEmergencyCredits/YourEmergencyCreditsPage";

interface RouteParams {
  shiftId: string;
}

interface LocationState {
  facility?: {
    id: string;
    name: string;
    type: string;
  };
  isDelayedWorkplaceReviewEnabled?: boolean;
  backgroundGradient?: string;
  scoreLogs?: ClipboardScoreLog[];
  currentScore?: number;
  scoreChange?: number;
  latenessMinutes: number;
  isSecondShiftInDouble?: boolean;
  isFirstShiftInDouble?: boolean;
  wasBookedWithShortLeadTime?: boolean;
  newlyRestricted?: boolean;
  isArrivalTimeFeatureEnabled?: boolean;
  formattedTargetArrivalTime?: string;
}

type FlowStep = "emergencyCredits" | "clipboardScore";

export function EmergencyCreditsClockOutFlowPage() {
  const { shiftId } = useParams<RouteParams>();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const postShiftReviewModalState = useModalState();
  const emergencyCreditsEducationModalState = useModalState();

  const { userId: workerId } = useDefinedWorker();
  const {
    facility,
    isDelayedWorkplaceReviewEnabled,
    backgroundGradient,
    scoreLogs,
    currentScore,
    scoreChange,
    latenessMinutes,
    isSecondShiftInDouble,
    isFirstShiftInDouble,
    wasBookedWithShortLeadTime,
    newlyRestricted,
    isArrivalTimeFeatureEnabled,
    formattedTargetArrivalTime,
  } = location.state ?? {};

  const [currentStep, setCurrentStep] = useState<FlowStep>("emergencyCredits");

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId);

  const emergencyCreditStats = useEmergencyCreditStats({ clipboardScoreProfile });
  const hasAvailableLatenessCredit = emergencyCreditStats?.hasAvailableLatenessCredit ?? false;

  const clipboardScoreData = useMemo(() => {
    if (!isDefined(scoreLogs) || !isDefined(currentScore) || !isDefined(scoreChange)) {
      return undefined;
    }

    return buildScoreDataForEmergencyCreditsClockOutFlow({
      scoreLogs,
      currentScore,
      scoreChange,
      latenessMinutes,
      isSecondShiftInDouble: isSecondShiftInDouble ?? false,
      isFirstShiftInDouble: isFirstShiftInDouble ?? false,
      wasBookedWithShortLeadTime: wasBookedWithShortLeadTime ?? false,
      newlyRestricted: newlyRestricted ?? false,
      isArrivalTimeFeatureEnabled: isArrivalTimeFeatureEnabled ?? false,
      formattedTargetArrivalTime,
      policy: clipboardScoreProfile?.policy,
      hasAvailableLatenessCredit,
    });
  }, [
    scoreLogs,
    currentScore,
    scoreChange,
    latenessMinutes,
    isSecondShiftInDouble,
    isFirstShiftInDouble,
    wasBookedWithShortLeadTime,
    newlyRestricted,
    isArrivalTimeFeatureEnabled,
    formattedTargetArrivalTime,
    clipboardScoreProfile?.policy,
    hasAvailableLatenessCredit,
  ]);

  const hasLatenessCredit =
    hasAvailableLatenessCredit && isDefined(clipboardScoreData?.savedPoints);

  const handleEmergencyCreditsContinue = useCallback((): void => {
    setCurrentStep("clipboardScore");
  }, []);

  const handleClipboardScoreContinue = useCallback((): void => {
    if ((isDelayedWorkplaceReviewEnabled ?? false) || !facility) {
      history.replace(MY_SHIFTS_FULL_PATH);
    } else {
      postShiftReviewModalState.openModal();
    }
  }, [history, isDelayedWorkplaceReviewEnabled, facility, postShiftReviewModalState]);

  const handlePostShiftReviewComplete = useCallback((): void => {
    postShiftReviewModalState.closeModal();
    history.replace(MY_SHIFTS_FULL_PATH);
  }, [history, postShiftReviewModalState]);

  const handleLearnMoreClick = useCallback((): void => {
    emergencyCreditsEducationModalState.openModal();
  }, [emergencyCreditsEducationModalState]);

  if (!isDefined(emergencyCreditStats)) {
    history.replace(MY_SHIFTS_FULL_PATH);
    return null;
  }

  const { cancellationCreditStats, latenessCreditStats } = emergencyCreditStats;

  if (currentStep === "clipboardScore") {
    if (!isDefined(clipboardScoreData)) {
      history.replace(MY_SHIFTS_FULL_PATH);
      return null;
    }

    return (
      <ThemeProvider theme={getTheme()}>
        <YourClipboardScorePage
          showCreditProgressBar
          startScore={clipboardScoreData.startScore}
          scoreChanges={clipboardScoreData.scoreChanges}
          subtitle={clipboardScoreData.subtitle}
          gradientVariant={clipboardScoreData.gradientVariant}
          creditProgressBarProps={{
            currentCount: emergencyCreditStats.completedShiftsToDisplayForLatenessCredit,
            totalCount: latenessCreditStats.requiredShiftsForNextCredit,
            label: getCreditProgressBarLabel("lateness", hasAvailableLatenessCredit),
            color: "green",
          }}
          creditAmount={clipboardScoreData.savedPoints}
          hasLatenessCredit={hasLatenessCredit}
          onContinue={handleClipboardScoreContinue}
        />

        {facility && (
          <PostShiftReviewFlow
            modalState={postShiftReviewModalState}
            facility={{
              id: facility.id,
              name: facility.name,
              type: facility.type,
              photo: undefined,
            }}
            shiftId={shiftId}
            onComplete={handlePostShiftReviewComplete}
          />
        )}
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <YourEmergencyCreditsPage
        backgroundGradient={backgroundGradient}
        cancellationStats={cancellationCreditStats}
        latenessStats={latenessCreditStats}
        onContinue={handleEmergencyCreditsContinue}
        onLearnMoreClick={handleLearnMoreClick}
      />

      {facility && (
        <PostShiftReviewFlow
          modalState={postShiftReviewModalState}
          facility={{
            id: facility.id,
            name: facility.name,
            type: facility.type,
            photo: undefined,
          }}
          shiftId={shiftId}
          onComplete={handlePostShiftReviewComplete}
        />
      )}
    </ThemeProvider>
  );
}
